import { requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * Tracks which grid-density control bar is actually on screen.
 *
 * Horizon's shared `mediaQueryLarge` breaks at 750px, but this store shows the
 * MOBILE control bar all the way up to 1199px: filters.liquid collapses the
 * filter rail into the drawer below 1200, and product-card.liquid renders
 * stacked cards over the same range.
 *
 * Keying this file off 750px meant that between 750px and 1199px the handler
 * below updated radios in the HIDDEN desktop group while the visible mobile
 * group kept its old selection — so the toggle showed "list" while the grid had
 * silently been forced back to "default". Rotating a phone to landscape hit
 * this every time.
 *
 * The two radio groups are independent (name="grid" and name="grid-mobile"),
 * so checking one never unchecks the other; the group that is visible has to be
 * the group that gets updated.
 *
 * If the 1200px boundary moves, it has to move in all three files:
 * filters.liquid, product-card.liquid, and here.
 */
const mediaQueryDesktopControls = window.matchMedia('(min-width: 1200px)');

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryDesktopControls.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    // PaginatedList.disconnectedCallback() disconnects the infinite-scroll
    // IntersectionObserver and removes two DOCUMENT-level listeners
    // (searchUpdate / collectionUpdate). This override previously did not call
    // it, while connectedCallback did call its super — so the pairing was
    // asymmetric and the parent's cleanup never ran.
    //
    // That leaks on every filter interaction, not just on page teardown: each
    // filter change re-renders the section, disconnecting this element and
    // connecting a fresh one. The new instance adds its document listeners; the
    // old instance never removed its own, so handlers accumulate and keep
    // firing against a detached grid.
    super.disconnectedCallback();

    mediaQueryDesktopControls.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryDesktopControls.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * Previously this force-set the layout to 'default' on every crossing, which
   * is why rotating a phone always dropped you back into the tile grid even if
   * you had deliberately chosen list view. It now restores the choice already
   * recorded for the viewport being entered, and updates the radio group that
   * belongs to that viewport so the control and the layout cannot disagree.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const groupName = event.matches ? 'grid' : 'grid-mobile';

    const inputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll(`input[type="radio"][name="${groupName}"]`)).filter(
        (input) => input instanceof HTMLInputElement
      )
    );

    if (inputs.length === 0) return;

    // Preference order: what this viewport last used, then whatever the group
    // is already showing (covers a first rotation with nothing stored yet),
    // then the group's default option. Matching on value rather than on
    // data-grid-layout is deliberate — both mobile options carry
    // data-grid-layout="mobile-option", so a lookup by that attribute always
    // returns whichever happens to come first in the DOM.
    const stored = sessionStorage.getItem(`product-grid-view-${viewport}`);

    const target =
      inputs.find((input) => input.value === stored) ??
      inputs.find((input) => input.checked) ??
      inputs.find((input) => input.value === 'default') ??
      inputs[0];

    if (!target) return;

    target.checked = true;
    this.#setLayout(target.value);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}